import { useState, useEffect, useCallback } from 'react';
import { Crosshair, TrendingUp, AlertTriangle, Zap } from 'lucide-react';
import { SeverityBadge } from './SEOOSShared';

const API = '';

export default function SEOOSCrawl() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('admin_token');

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/seoos/crawl`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !data || data.error) return <div className="text-center py-16 text-slate-500">{data?.error || 'Loading...'}</div>;

  const { crawl_efficiency_pct, waste_ratio_pct, total_crawlable, segments, issues, recommendations, authority_distribution } = data;

  return (
    <div className="space-y-6" data-testid="seoos-crawl">
      <h2 className="text-xl font-black text-white" style={{ fontFamily: 'var(--font-heading)' }}>
        <Crosshair className="w-5 h-5 inline mr-2 text-purple-400" />Crawl Intelligence
      </h2>

      {/* Top metrics */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-emerald-950/30 to-slate-900 border border-emerald-500/20 rounded-xl p-5 text-center">
          <p className="text-4xl font-black text-emerald-400" style={{ fontFamily: 'var(--font-heading)' }}>{crawl_efficiency_pct}%</p>
          <p className="text-xs text-slate-400 mt-1">Crawl Efficiency</p>
        </div>
        <div className="bg-gradient-to-br from-red-950/30 to-slate-900 border border-red-500/20 rounded-xl p-5 text-center">
          <p className="text-4xl font-black text-red-400" style={{ fontFamily: 'var(--font-heading)' }}>{waste_ratio_pct}%</p>
          <p className="text-xs text-slate-400 mt-1">Waste Ratio</p>
        </div>
        <div className="bg-gradient-to-br from-blue-950/30 to-slate-900 border border-blue-500/20 rounded-xl p-5 text-center">
          <p className="text-4xl font-black text-blue-400" style={{ fontFamily: 'var(--font-heading)' }}>{total_crawlable?.toLocaleString()}</p>
          <p className="text-xs text-slate-400 mt-1">Total Crawlable Pages</p>
        </div>
      </div>

      {/* Segments */}
      <div className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-5">
        <h3 className="text-sm font-bold text-white uppercase mb-4">Crawl Budget Distribution</h3>
        {Object.entries(segments || {}).map(([key, seg]) => {
          const pct = ((seg.pages / Math.max(total_crawlable, 1)) * 100).toFixed(1);
          const barColor = key === 'high_value' ? 'bg-emerald-500' : key === 'medium_value' ? 'bg-blue-500' : key === 'low_value' ? 'bg-amber-500' : 'bg-red-500';
          return (
            <div key={key} className="mb-3">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-300">{seg.label}</span>
                <span className="text-white font-bold">{seg.pages.toLocaleString()} ({pct}%)</span>
              </div>
              <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                <div className={`h-full ${barColor} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Issues */}
      <div className="space-y-3">
        <h3 className="text-sm font-bold text-white uppercase">Crawl Issues</h3>
        {(issues || []).map((issue, i) => (
          <div key={i} className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <SeverityBadge severity={issue.severity} />
              <span className="text-sm font-bold text-white">{issue.title}</span>
            </div>
            <p className="text-xs text-slate-400 mb-2">{issue.detail}</p>
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-slate-600">Fix: {issue.fix}</span>
              <span className="text-xs font-bold text-red-400">{issue.waste_pages} wasted pages</span>
            </div>
          </div>
        ))}
      </div>

      {/* Recommendations */}
      <div className="bg-slate-900/40 border border-purple-500/20 rounded-xl p-5">
        <h3 className="text-sm font-bold text-purple-300 uppercase mb-3"><Zap className="w-4 h-4 inline mr-1" />Oneriler</h3>
        <div className="space-y-2">
          {(recommendations || []).map((rec, i) => (
            <div key={i} className="flex items-center gap-3 text-xs">
              <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">{rec.priority}</span>
              <span className="text-slate-300 flex-1">{rec.action}</span>
              <span className="text-emerald-400 text-[10px]">{rec.expected_impact}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
